import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Seo } from '../../components/Seo';
import { MapPin, Award, ExternalLink, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const CityAuctioneers = () => {
  const { state, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/state/${state}/city/${city}`)
      .then((r) => {
        if (!r.ok) throw new Error('nf');
        return r.json();
      })
      .then((d) => setData(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [state, city]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-gray-600" data-testid="city-not-found">
          No auctioneers found in this city.
        </div>
        <Footer />
      </>
    );
  }

  const cityName = data?.city || '';
  const stateName = data?.state_name || '';
  const total = data ? data.auctioneers.length + data.houses.length : 0;
  const featured = data ? data.auctioneers.filter((a) => a.is_premier) : [];

  const jsonLd = data && [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Find an Auctioneer', item: 'https://netauctionsinc.com/find-an-auctioneer' },
        { '@type': 'ListItem', position: 2, name: stateName, item: `https://netauctionsinc.com/find-an-auctioneer/${data.state_slug}` },
        { '@type': 'ListItem', position: 3, name: cityName, item: `https://netauctionsinc.com/find-an-auctioneer/${data.state_slug}/${data.city_slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: data.auctioneers.slice(0, 25).map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://netauctionsinc.com/auctioneer/${a.slug}`,
        name: a.name,
      })),
    },
  ];

  return (
    <>
      <Navbar />
      {data && (
        <Seo
          title={`${cityName}, ${data.state} Auctioneers — ${total} Listed | Net Auctions`}
          description={`Find a licensed auctioneer in ${cityName}, ${stateName}. ${total} vetted auctioneers and auction companies for estate sales, real estate, farm equipment and business liquidations. Free directory.`}
          canonicalPath={`/find-an-auctioneer/${data.state_slug}/${data.city_slug}`}
          jsonLd={jsonLd}
        />
      )}
      <div className="min-h-screen bg-gray-50" data-testid="city-page">
        <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-green-200 text-sm mb-3">
              <Link to="/find-an-auctioneer" className="hover:text-white">Find an Auctioneer</Link>
              <span className="mx-2">/</span>
              {data && <Link to={`/find-an-auctioneer/${data.state_slug}`} className="hover:text-white">{stateName}</Link>}
              <span className="mx-2">/</span>
              <span>{cityName}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">
              {data ? `Auctioneers in ${cityName}, ${data.state}` : 'Auctioneers'}
            </h1>
            <p className="text-lg text-green-100 max-w-3xl">
              {total} licensed auctioneers and auction companies serving {cityName}, {stateName}.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : (
            <>
              {featured.length > 0 ? (
                <section data-testid="featured-slot">
                  <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-500" /> Featured Auctioneer in {cityName}
                  </h2>
                  <div className="grid grid-cols-1 gap-4">
                    {featured.slice(0, 1).map((a) => (
                      <Link
                        key={a.id}
                        to={`/auctioneer/${a.slug}`}
                        data-testid={`featured-card-${a.slug}`}
                        className="block bg-gradient-to-r from-yellow-50 to-white rounded-xl p-6 border-2 border-yellow-400 hover:shadow-lg transition-all"
                      >
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-yellow-700 mb-2">
                          <Award className="w-3.5 h-3.5" /> PREMIER · FEATURED
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900">{a.business_name || a.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4 text-green-600" /> {a.city}, {a.state}
                        </p>
                        {a.specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {a.specialties.slice(0, 5).map((s, i) => (
                              <span key={i} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-yellow-200">{s}</span>
                            ))}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </section>
              ) : (
                <section data-testid="featured-cta"
                  className="bg-gradient-to-r from-green-700 to-green-800 text-white rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-yellow-300 text-sm font-bold mb-1">
                      <Award className="w-4 h-4" /> PREMIER SPOT AVAILABLE
                    </div>
                    <h2 className="text-lg md:text-lg font-bold">Be the Featured Auctioneer in {cityName}</h2>
                    <p className="text-green-100 text-sm mt-1">
                      Appear at the top of this page and every {cityName} search — just $59.88/yr. One exclusive slot per city.
                    </p>
                  </div>
                  <Link
                    to="/auctioneer/login"
                    data-testid="become-featured-btn"
                    className="whitespace-nowrap px-6 py-3 bg-yellow-400 text-yellow-900 font-bold rounded-full hover:bg-yellow-300 transition-colors"
                  >
                    Claim this spot →
                  </Link>
                </section>
              )}

              {data.auctioneers.length > 0 && (
                <section data-testid="city-auctioneers">
                  <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-4">Individual Auctioneers</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.auctioneers.map((a) => (
                      <Link
                        key={a.id}
                        to={`/auctioneer/${a.slug}`}
                        data-testid={`auctioneer-card-${a.slug}`}
                        className={`block bg-white rounded-xl p-5 border transition-all hover:shadow-md ${a.is_premier ? 'border-yellow-400' : 'border-gray-200 hover:border-green-500'}`}
                      >
                        {a.is_premier && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-yellow-700 mb-2">
                            <Award className="w-3.5 h-3.5" /> PREMIER
                          </span>
                        )}
                        <h3 className="text-lg font-bold text-gray-900">{a.business_name || a.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4 text-green-600" /> {a.city}, {a.state}
                        </p>
                        {a.specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {a.specialties.slice(0, 3).map((s, i) => (
                              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">{s}</span>
                            ))}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {data.houses.length > 0 && (
                <section data-testid="city-houses">
                  <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-4">Auction Companies</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.houses.map((h) => (
                      <div key={h.id} className="bg-white rounded-xl p-5 border border-gray-200" data-testid={`house-card-${h.id}`}>
                        <h3 className="text-lg font-bold text-gray-900">{h.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4 text-green-600" /> {h.location?.city}, {h.location?.state}
                        </p>
                        {h.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{h.description}</p>}
                        <div className="flex gap-3 mt-3">
                          {h.url && (
                            <a href={h.url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 flex items-center gap-1 hover:underline">
                              <ExternalLink className="w-4 h-4" /> Website
                            </a>
                          )}
                          {h.phone && (
                            <a href={`tel:${h.phone}`} className="text-sm text-gray-600 flex items-center gap-1 hover:underline">
                              <Phone className="w-4 h-4" /> {h.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};
